import React, { useRef } from "react"
import { View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { ModalM } from "@common/components/modal/ModalM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import IconButtonM from "@common/components/button/IconButtonM"
import TextM from "@common/components/text/TextM"
import { IconImageM } from "@common/components/icons/IconsM"
import { FontAwesome5 } from "@expo/vector-icons"
import { theme } from "@common/theme/theme"
import { isPdfFile } from "@common/components/pdf/pdfHelpers"
import { IS_WEB } from "@common/utils/mobileUtils"
import { PinchableBox } from "@common/components/image/PinchableBox"

/**
 * MediaPreviewModal - Simple modal to preview media with action buttons
 *
 * Two modes:
 * 1. Existing message (isExistingMessage=true): Shows "Créer une activité" button only
 * 2. New file (isExistingMessage=false): Shows "Créer une activité" + "Envoyer dans le chat" buttons
 */
const MediaPreviewModal = ({
    visible,
    onDismiss,
    file,
    isExistingMessage = false,
    onCreateActivity,
    onSendToChat,
    sendingToChat = false,
    dimensions,
    images = [],
    currentIndex = 0,
    onNavigate,
}) => {
    const zoomRef = useRef(null)

    if (!file) return null

    const isPDF = isPdfFile(file)

    // Arrows to flip between every image of the conversation (disabled on PDF,
    // which is always a single file)
    const canNavigate = !isPDF && images?.length > 1 && !!onNavigate

    const goToIndex = (newIndex) => {
        if (newIndex < 0 || newIndex >= images.length) return
        zoomRef.current?.zoomOut()
        onNavigate(newIndex)
    }

    const modalWidth = IS_WEB ? Math.min(600, dimensions?.width - 40) : dimensions?.width * 0.9
    const modalHeight = IS_WEB ? Math.min(650, dimensions?.height - 80) : dimensions?.height * 0.75

    // Calculate heights: header (60) + buttons area (120 or 180) + padding
    const headerHeight = 60
    const buttonsHeight = isExistingMessage ? 90 : 150
    const previewHeight = modalHeight - headerHeight - buttonsHeight

    return (
        <ModalM
            visible={visible}
            onDismiss={onDismiss}
            contentContainerStyle={{
                width: modalWidth,
                maxHeight: modalHeight,
                backgroundColor: theme.colors.white,
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    height: headerHeight,
                    borderBottomWidth: 1,
                    borderBottomColor: theme.colors.lightMore,
                }}
            >
                <IconButtonM
                    icon="times"
                    iconColor={theme.colors.primary}
                    containerColor={theme.colors.transparent}
                    size={24}
                    onPress={onDismiss}
                    style={{ marginRight: 8 }}
                />
                <TextM
                    fontSize={16}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                >
                    {isExistingMessage ? "Aperçu" : "Nouveau fichier"}
                </TextM>
                {canNavigate && (
                    <TextM
                        fontSize={14}
                        color={theme.colors.grey}
                        style={{ marginLeft: "auto" }}
                    >
                        {currentIndex + 1} / {images.length}
                    </TextM>
                )}
            </View>

            {/* Media Preview */}
            <View
                style={{
                    backgroundColor: theme.colors.lighter,
                    padding: 15,
                    height: previewHeight,
                }}
            >
                {isPDF ? (
                    <View
                        style={{
                            flex: 1,
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 15,
                        }}
                    >
                        <FontAwesome5
                            name="file-pdf"
                            size={64}
                            color={theme.colors.error}
                        />
                        <TextM
                            fontSize={14}
                            color={theme.colors.grey}
                            style={{ textAlign: "center" }}
                        >
                            {file?.name || "Document PDF"}
                        </TextM>
                    </View>
                ) : (
                    <View style={{ flex: 1 }}>
                        <PinchableBox
                            imageUri={file?.uri}
                            imageStyle={{
                                width: "100%",
                            }}
                            ref={zoomRef}
                        />
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                marginTop: 10,
                            }}
                        >
                            {canNavigate && (
                                <IconButtonM
                                    size={36}
                                    icon="angle-left"
                                    iconColor={theme.colors.primary}
                                    disabled={currentIndex === 0}
                                    onPress={() => goToIndex(currentIndex - 1)}
                                />
                            )}
                            <IconButtonM
                                size={30}
                                icon="minus-circle"
                                iconColor={theme.colors.grey}
                                onPress={() => zoomRef.current?.zoomOut()}
                            />
                            <IconButtonM
                                size={30}
                                icon="redo"
                                iconColor={theme.colors.grey}
                                onPress={() => zoomRef.current?.rotate()}
                            />
                            <IconButtonM
                                size={30}
                                icon="plus-circle"
                                iconColor={theme.colors.grey}
                                onPress={() => zoomRef.current?.zoomIn()}
                            />
                            {canNavigate && (
                                <IconButtonM
                                    size={36}
                                    icon="angle-right"
                                    iconColor={theme.colors.primary}
                                    disabled={
                                        currentIndex === images.length - 1
                                    }
                                    onPress={() => goToIndex(currentIndex + 1)}
                                />
                            )}
                        </View>
                    </View>
                )}
            </View>

            {/* Action Buttons */}
            <View
                style={{
                    padding: 16,
                    paddingBottom: 20,
                    gap: 10,
                    minHeight: buttonsHeight,
                }}
            >
                {/* Primary action: Create activity */}
                <FilledButtonM
                    label="Créer une activité"
                    icon="magic"
                    onPress={onCreateActivity}
                    color={theme.colors.primary}
                    style={{ width: "100%" }}
                />

                {/* Secondary action: Send to chat (new files only) */}
                {!isExistingMessage && (
                    <FilledButtonM
                        label="Envoyer dans le chat"
                        icon="paper-plane"
                        onPress={onSendToChat}
                        mode="outlined"
                        color={theme.colors.primary}
                        style={{ width: "100%" }}
                        loading={sendingToChat}
                        disabled={sendingToChat}
                    />
                )}
            </View>
        </ModalM>
    )
}

export default MediaPreviewModal
